import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldAlert, ShieldCheck, Mail, Download } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { api, errMsg, fmtDateTime } from "@/lib/api";

export default function ReportAccess() {
  const { token } = useParams();
  const [d, setD] = useState<any>(null);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/public/report-link/${token}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e))); }, [token]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setMsg("");
    try { await fn(); } catch (e: any) {
      if (e?.response?.data instanceof Blob) { try { setMsg(JSON.parse(await e.response.data.text()).detail); } catch { setMsg("Something went wrong."); } }
      else setMsg(errMsg(e));
    }
    setBusy(false);
  };
  const sendCode = () => run(async () => { await api.post(`/public/report-link/${token}/send-code`); setSent(true); setCode(""); });
  const [file, setFile] = useState("");
  const download = () => run(async () => {
    const r = await api.post(`/public/report-link/${token}/download`, { code }, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `report-${d.case_ref}.pdf`; a.click();
    setFile(url);
    setDone(true); setSent(false); setCode("");
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-lg px-4 py-16 sm:px-6">
        {error ? (
          <div className="card-soft flex items-start gap-3 p-6 text-[#991B1B]" data-testid="report-access-error"><ShieldAlert className="h-5 w-5 shrink-0" /> {error}</div>
        ) : !d ? (
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#F26B4F]" />
        ) : (
          <div className="card-soft p-8" data-testid="report-access-card">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#E8573A]">Secure report</p>
            <h1 className="mt-1 text-4xl font-extrabold tracking-tight" data-testid="report-access-case-ref">Case {d.case_ref}</h1>
            <p className="mt-2 text-sm text-[#64748B]">{d.organization} · link works until {fmtDateTime(d.expires_at)}</p>
            {done && <p className="mt-6 flex items-center gap-2 rounded-xl bg-[#D5F5EE] px-4 py-3 text-sm font-semibold text-[#0F766E]" data-testid="report-access-success"><ShieldCheck className="h-4 w-4" /> Report downloaded. Request a new code to download it again.</p>}
            {done && file && <a href={file} target="_blank" rel="noreferrer" className="btn-secondary mt-3 w-full" data-testid="report-access-open-button"><Download className="h-4 w-4" /> Didn't see it? Tap here to open the report</a>}
            {!sent ? (
              <div className="mt-6">
                <p className="text-sm text-[#1A2536]">To confirm it is you, we will email a 6-digit code to <b>{d.email}</b>.</p>
                <button className="btn-primary mt-4 w-full" disabled={busy} onClick={sendCode} data-testid="report-access-send-code-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email me a code
                </button>
              </div>
            ) : (
              <form className="mt-6" onSubmit={(e) => { e.preventDefault(); download(); }}>
                <label className="text-sm font-semibold text-[#1A2536]" htmlFor="report-code">Code sent to {d.email}</label>
                <input id="report-code" className="field-input mt-2 text-center text-2xl tracking-[0.5em]" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="report-access-code-input" />
                <p className="mt-2 text-xs text-[#64748B]" data-testid="report-access-code-hint">Use the newest code in your inbox. Each new code cancels the one before it. The code works for 10 minutes.</p>
                <button className="btn-primary mt-4 w-full" disabled={busy || code.length !== 6} data-testid="report-access-download-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download report
                </button>
                <button type="button" className="mt-3 w-full text-sm font-semibold text-[#1B365D] hover:underline" disabled={busy} onClick={sendCode} data-testid="report-access-resend-code-button">Send a new code</button>
              </form>
            )}
            {msg && <p className="mt-4 text-sm font-semibold text-[#991B1B]" data-testid="report-access-message">{msg}</p>}
          </div>
        )}
      </div>
    </PublicShell>
  );
}
